import { useState, useCallback } from 'react';
import { ProjectFull, ProjectSummary, StepData, EpisodeDetail, TemplateData } from '../types';

const DB_NAME = 'StoryForgeDB';
const DB_VERSION = 1;

export const DB_CONFIG = {
  name: DB_NAME,
  version: DB_VERSION,
  stores: {
    projects: {
      keyPath: 'projectId',
      indexes: [
        { name: 'author', keyPath: 'author' },
        { name: 'status', keyPath: 'status' },
        { name: 'updatedAt', keyPath: 'updatedAt' }
      ]
    },
    steps: {
      keyPath: 'stepId',
      indexes: [
        { name: 'projectId', keyPath: 'projectId' },
        { name: 'stepNum', keyPath: 'stepNum' }
      ]
    },
    episodes: {
      keyPath: 'episodeId',
      indexes: [
        { name: 'projectId', keyPath: 'projectId' }
      ]
    },
    templates: {
      keyPath: 'templateId',
      indexes: [
        { name: 'type', keyPath: 'type' },
        { name: 'author', keyPath: 'author' }
      ]
    }
  }
};

class IndexedDBService {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_CONFIG.name, DB_CONFIG.version);

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        Object.entries(DB_CONFIG.stores).forEach(([storeName, config]) => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: config.keyPath as string });
            config.indexes?.forEach(index => {
              store.createIndex(index.name, index.keyPath as string);
            });
          }
        });
      };

      request.onerror = () => reject(request.error);
    });
  }

  private getStore(name: string, mode: IDBTransactionMode): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    return this.db.transaction(name, mode).objectStore(name);
  }

  // Project CRUD
  async saveProject(data: ProjectFull): Promise<void> {
    const store = this.getStore('projects', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put({ ...data, updatedAt: new Date().toISOString() });
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getProject(projectId: string): Promise<ProjectFull | undefined> {
    const store = this.getStore('projects', 'readonly');
    return new Promise((resolve, reject) => {
      const request = store.get(projectId);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  async getAllProjects(): Promise<ProjectSummary[]> {
    const store = this.getStore('projects', 'readonly');
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  async deleteProject(projectId: string): Promise<void> {
    const store = this.getStore('projects', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(projectId);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  // Step CRUD
  async saveStep(projectId: string, stepNum: number, data: StepData): Promise<void> {
    const stepId = `${projectId}_step${stepNum}`;
    const store = this.getStore('steps', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put({
        stepId,
        projectId,
        stepNum,
        data,
        updatedAt: new Date().toISOString()
      });
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  // Episode CRUD
  async saveEpisode(projectId: string, episodeNum: number, data: EpisodeDetail): Promise<void> {
    const episodeId = `${projectId}_episode${episodeNum}`;
    const store = this.getStore('episodes', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put({
        episodeId,
        projectId,
        episodeNum,
        data,
        updatedAt: new Date().toISOString()
      });
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getAllEpisodes(projectId: string): Promise<EpisodeDetail[]> {
    const store = this.getStore('episodes', 'readonly');
    const index = store.index('projectId');
    return new Promise((resolve, reject) => {
      const request = index.getAll(projectId);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result.map(r => r.data));
    });
  }

  // Template CRUD
  async saveTemplate(templateId: string, type: string, data: TemplateData): Promise<void> {
    const store = this.getStore('templates', 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put({
        templateId,
        type,
        data,
        author: 'system',
        createdAt: new Date().toISOString()
      });
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getTemplate(templateId: string): Promise<TemplateData | undefined> {
    const store = this.getStore('templates', 'readonly');
    return new Promise((resolve, reject) => {
      const request = store.get(templateId);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result?.data);
    });
  }

  async getTemplatesByType(type: string): Promise<TemplateData[]> {
    const store = this.getStore('templates', 'readonly');
    const index = store.index('type');
    return new Promise((resolve, reject) => {
      const request = index.getAll(type);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result.map(r => r.data));
    });
  }

  // Export/Import
  async exportProject(projectId: string): Promise<string> {
    const project = await this.getProject(projectId);
    if (!project) throw new Error('Project not found');

    const episodes = await this.getAllEpisodes(projectId);

    const exportData = {
      project,
      episodes,
      exportedAt: new Date().toISOString(),
      version: DB_VERSION
    };

    return JSON.stringify(exportData, null, 2);
  }

  async importProject(jsonData: string): Promise<string> {
    const data = JSON.parse(jsonData);
    const project = data.project as ProjectFull;

    // Generate new ID if project with same ID exists
    const existingProject = await this.getProject(project.projectId);
    if (existingProject) {
      project.projectId = crypto.randomUUID();
    }

    await this.saveProject(project);

    // Import episodes
    if (data.episodes) {
      for (const episode of data.episodes) {
        await this.saveEpisode(project.projectId, episode.episodeNumber, episode);
      }
    }

    return project.projectId;
  }
}

// Singleton instance
export const indexedDBService = new IndexedDBService();

// React Hook
export const useIndexedDB = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const init = useCallback(async () => {
    try {
      await indexedDBService.init();
      setIsInitialized(true);
    } catch (err) {
      setError(err as Error);
      console.error('IndexedDB initialization error:', err);
    }
  }, []);

  return {
    init,
    isInitialized,
    error,
    service: indexedDBService
  };
};
